import { paceLabel, type PlayingPace } from '@league/shared/schemas';
import { Button } from '../../components/ui';
import type { SeatDrag } from './useSeatDrag';

/** Unseated eligible players remain part of the draft, separate from absences and waitlist. */
interface Person {
  enrollmentId: string;
  name: string;
  firstName?: string;
  lastName?: string;
  status: 'enrolled' | 'registered' | 'waitlist';
  skillLevel: string;
  pace?: PlayingPace;
  isSubstitute?: boolean;
}

export function NotSeated({
  roster,
  seatedIds,
  awayIds,
  picked,
  drag,
  onPick,
  onSetAside,
  disabled,
}: {
  onSetAside: (id: string) => void;
  disabled: boolean;
  roster: Person[];
  seatedIds: Set<string>;
  awayIds: Set<string>;
  picked: string | null;
  /** Null once the arrangement is published — nothing moves onto the board after that. */
  drag: SeatDrag | null;
  onPick: (id: string | null) => void;
}) {
  /** Eligible participants awaiting a chair. */
  const unseated = roster.filter(
    (p) =>
      p.status === 'enrolled' && !seatedIds.has(p.enrollmentId) && !awayIds.has(p.enrollmentId),
  ).sort((a,b) => (a.firstName ?? a.name).localeCompare(b.firstName ?? b.name) || (a.lastName ?? '').localeCompare(b.lastName ?? '') || a.enrollmentId.localeCompare(b.enrollmentId));

  const selected = picked && seatedIds.has(picked) ? roster.find(p => p.enrollmentId === picked) : null;
  const receiving = !disabled && drag?.dragging?.over === 'waiting' && seatedIds.has(drag.dragging.enrollmentId);

  return (
    <section data-chair={disabled ? undefined : 'waiting'} className={`flex flex-col gap-3 rounded-md border bg-paper p-3 ${receiving ? 'border-deep ring-2 ring-deep/30' : 'border-light'}`}>
      <h2 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-3">
        Players to seat ({unseated.length})
      </h2>
      {!disabled && selected ? <Button variant="secondary" className="h-auto min-h-11 max-w-full self-start py-2 text-left" onClick={() => {
        if (drag?.swallowClick()) return;
        onSetAside(selected.enrollmentId);
      }}>Move {selected.name} to Players to seat</Button> : null}
      {unseated.length === 0 ? <p className="text-[13px] text-ink-2">Set players aside here while rearranging.</p> : (
        <Group people={unseated} picked={picked} drag={disabled ? null : drag} onPick={onPick} quiet={disabled} />
      )}

    </section>
  );
}

function Group({
  label,
  note,
  people,
  picked,
  drag,
  onPick,
  quiet,
}: {
  label?: string;
  note?: string;
  people: Person[];
  picked: string | null;
  drag: SeatDrag | null;
  onPick: (id: string | null) => void;
  quiet?: boolean;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      {label ? <p className="text-[12px] font-medium text-ink-2">{label}</p> : null}
      {note ? <p className="text-[12px] text-ink-3">{note}</p> : null}

      <div className="flex max-h-60 flex-wrap gap-1.5 overflow-y-auto pt-0.5">
        {people.map((person) =>
          /*
            The away are listed but not pickable. Seating somebody who is not coming is not a move
            a director means to make, and a button that looks like the others but produces a table
            with a ghost at it is worse than no button.
          */
          quiet ? (
            <span
              key={person.enrollmentId}
              className="rounded-card border border-light px-2.5 py-1 text-[13px] text-ink-3"
            >
              {person.name}<span className="block text-[11px]">{paceLabel(person.pace)}</span>
            </span>
          ) : (
            <Button
              key={person.enrollmentId}
              size="inline"
              variant={picked === person.enrollmentId ? 'primary' : 'secondary'}
              aria-pressed={picked === person.enrollmentId}
              className={[
                'h-auto min-h-11 flex-col py-2',
                drag ? 'cursor-grab' : '',
                /** Dimmed while she is in the air, so the list reads as one short rather than two of her. */
                drag?.dragging?.enrollmentId === person.enrollmentId ? 'opacity-40' : '',
              ].join(' ')}
              onPointerDown={(event) => drag?.start(person.enrollmentId, person.name, event)}
              onClick={() => {
                /** A drag ends in a click the browser invents; taking it would re-pick her up. */
                if (drag?.swallowClick()) return;
                onPick(picked === person.enrollmentId ? null : person.enrollmentId);
              }}
            >
              <span>{person.name}{person.isSubstitute ? " · Substitute" : ""}</span>
              <span className={`text-[11px] font-normal ${picked === person.enrollmentId ? '' : 'text-ink-2'}`}>{paceLabel(person.pace)}</span>
            </Button>
          ),
        )}
      </div>
    </div>
  );
}
